import { useRef, useState } from "react";
import { isAxiosError } from "axios";
import axiosInstance from "../libs/axiosInstance";
import { toast } from "sonner";


const SUPPORTED_FILE_TYPES = ["csv", "xlsx", "json"];

const getErrorMessage = (error) => {
  if (!isAxiosError(error)) {
    return "Failed to upload questions. Please try again.";
  }

  if (error.code === "ECONNABORTED") {
    return "Upload timed out. Please try again with a smaller file.";
  }

  const responseData = error.response?.data;
  const validationErrors = responseData?.errors;

  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const firstError = validationErrors[0];
    if (typeof firstError === "string") {
      return firstError;
    }
    if (typeof firstError?.message === "string") {
      return firstError.message;
    }
  }

  if (typeof responseData?.message === "string") {
    return responseData.message;
  }

  if (typeof responseData?.detail === "string") {
    return responseData.detail;
  }

  if (error.response?.status === 413) {
    return "The selected file is too large to upload.";
  }

  if (!error.response) {
    return "Network error. Check your connection and try again.";
  }

  return "Failed to upload questions. Please try again.";
};

const clearSelectedFile = (fileInputRef, setSelectedFile) => {
  setSelectedFile(null);
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};

const QuestionFileUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !SUPPORTED_FILE_TYPES.includes(fileExtension)) {
      toast.error("Unsupported file format. Use CSV, XLSX, or JSON.");
      clearSelectedFile(fileInputRef, setSelectedFile);
      return;
    }

    setSelectedFile(file);
  };

  const handleClose = () => {
    if (isUploading) {
      return;
    }

    clearSelectedFile(fileInputRef, setSelectedFile);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error("Select a file before uploading.");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axiosInstance.post(
        '/question_bank/questions/upload/',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        }
      );

      toast.success('Questions uploaded successfully!');
      toast.info(`Valid: ${response.data.valid_rows}, Invalid: ${response.data.invalid_rows} (Total: ${response.data.total_rows})`);

      if (onUploadSuccess) onUploadSuccess(response.data.data);
      clearSelectedFile(fileInputRef, setSelectedFile);
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box relative">
        <button className="btn btn-sm btn-circle absolute right-4 top-4" onClick={handleClose} disabled={isUploading}>✕</button>
        <h3 className="font-semibold text-lg mb-4">Upload Questions File</h3>
        <div className="mb-4 rounded-lg border border-base-300 bg-base-200/60 p-3 text-sm">
          <p className="mb-2">Download the CSV template before uploading if you need the expected format.</p>
          <a
            href={'/Template of Bulk Upload - Sheet1.csv'}
            target="_blank"
            rel="noreferrer"
            className="link link-primary font-medium"
          >
            Download CSV template
          </a>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.json"
          className="file-input file-input-bordered w-full"
          disabled={isUploading}
        />
        {selectedFile && (
          <p className="mt-2 text-sm text-base-content/70">
            Selected file: {selectedFile.name}
          </p>
        )}
        <div className="modal-action">
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isUploading || !selectedFile}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
          <button className="btn btn-ghost" onClick={handleClose} disabled={isUploading}>
            Cancel
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default QuestionFileUploadModal;